import { useEffect, useState, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import Home from "./pages/Home";
import ModelDetail from "./pages/ModelDetail";
import AllModels from "./pages/AllModels";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

// Force-remount detail pages on slug change so the old image cannot flash
function ModelDetailKeyed() {
  const { slug } = useParams();
  return <ModelDetail key={slug} />;
}
function BlogPostKeyed() {
  const { slug } = useParams();
  return <BlogPost key={slug} />;
}

// Admin is code-split — not loaded for public site visitors.
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPostsList = lazy(() => import("./pages/admin/AdminPostsList"));
const AdminPostEdit = lazy(() => import("./pages/admin/AdminPostEdit"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPageSeo = lazy(() => import("./pages/admin/AdminPageSeo"));
const AdminFiles = lazy(() => import("./pages/admin/AdminFiles"));
const AdminAccount = lazy(() => import("./pages/admin/AdminAccount"));
const AdminHeroSlider = lazy(() => import("./pages/admin/AdminHeroSlider"));
const AdminHandovers = lazy(() => import("./pages/admin/AdminHandovers"));
const AdminSiteContent = lazy(() => import("./pages/admin/AdminSiteContent"));
const AdminModels = lazy(() => import("./pages/admin/AdminModels"));
const ProtectedAdminRoute = lazy(() => import("./pages/admin/ProtectedAdminRoute"));

import { SettingsProvider } from "./lib/SettingsContext";
import { ContentProvider } from "./lib/ContentContext";
import { AdminAuthProvider } from "./lib/AdminAuthContext";
import AnalyticsInjector from "./lib/AnalyticsInjector";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

const AdminLoading = (
  <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]">
    <p className="text-sm text-[#86868B]">Memuat admin…</p>
  </div>
);

export default function App() {
  return (
    <div className="App">
      <SettingsProvider>
        <ContentProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AnalyticsInjector />
              <Routes>
                {/* Public site */}
                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/models" element={<PublicLayout><AllModels /></PublicLayout>} />
                <Route path="/model/:slug" element={<PublicLayout><ModelDetailKeyed /></PublicLayout>} />
                <Route path="/kebijakan-privasi" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
                <Route path="/blog/:slug" element={<PublicLayout><BlogPostKeyed /></PublicLayout>} />

                {/* Admin (lazy) */}
                <Route
                  path="/admin/login"
                  element={
                    <Suspense fallback={AdminLoading}>
                      <AdminLogin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <Suspense fallback={AdminLoading}>
                      <ProtectedAdminRoute>
                        <AdminLayout />
                      </ProtectedAdminRoute>
                    </Suspense>
                  }
                >
                  <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
                  <Route path="hero-slider" element={<Suspense fallback={null}><AdminHeroSlider /></Suspense>} />
                  <Route path="handovers" element={<Suspense fallback={null}><AdminHandovers /></Suspense>} />
                  <Route path="content" element={<Suspense fallback={null}><AdminSiteContent /></Suspense>} />
                  <Route path="models" element={<Suspense fallback={null}><AdminModels /></Suspense>} />
                  <Route path="posts" element={<Suspense fallback={null}><AdminPostsList /></Suspense>} />
                  <Route path="posts/:id" element={<Suspense fallback={null}><AdminPostEdit /></Suspense>} />
                  <Route path="page-seo" element={<Suspense fallback={null}><AdminPageSeo /></Suspense>} />
                  <Route path="files" element={<Suspense fallback={null}><AdminFiles /></Suspense>} />
                  <Route path="settings" element={<Suspense fallback={null}><AdminSettings /></Suspense>} />
                  <Route path="account" element={<Suspense fallback={null}><AdminAccount /></Suspense>} />
                </Route>

                <Route path="*" element={<PublicLayout><Home /></PublicLayout>} />
              </Routes>
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </AdminAuthProvider>
        </ContentProvider>
      </SettingsProvider>
    </div>
  );
}
